/**
 * Safely evaluate mathematical expressions with context variables
 * Sanitizes input and uses constant context injection to prevent code injection
 * @param {string} expression - Math expression (e.g., "unitPrice * quantity * 0.9")
 * @param {object} context - Variables available in expression
 * @returns {number} Evaluated result (0 on error)
 */
export default function createSafeEvaluator(expression, context) {
  const createMathEval = (expr, ctx) => {
    // Allow: alphanumeric, dots, operators (+, -, *, /, **, %, ^), parentheses, commas, whitespace
    const sanitized = expr.replace(/[^a-zA-Z0-9._+\-*/%^(),$\s]/g, '');

    // Build variable declarations for safe context
    const variableDeclarations = Object.keys(ctx)
      .map(k => {
        const val = ctx[k];
        return typeof val === 'number' || typeof val === 'boolean'
          ? `const ${k} = ${val};`
          : `const ${k} = ${JSON.stringify(val)};`;
      })
      .join('');

    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(
        'Math',
        'sqrt',
        'pow',
        'abs',
        'round',
        'floor',
        'ceil',
        'min',
        'max',
        'sin',
        'cos',
        'tan',
        'exp',
        'log',
        'log10',
        variableDeclarations + `return ${sanitized}`
      );

      return fn(
        Math,
        Math.sqrt,
        Math.pow,
        Math.abs,
        Math.round,
        Math.floor,
        Math.ceil,
        Math.min,
        Math.max,
        Math.sin,
        Math.cos,
        Math.tan,
        Math.exp,
        Math.log,
        Math.log10
      );
    } catch (e) {
      // console.warn(`Safe eval failed for "${expr}":`, e);
      return 0;
    }
  };
  return createMathEval(expression, context);
}
